// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { components } from "@/api/schema";

export type StoreHealthStatus = components["schemas"]["StoreHealthStatusEnum"];
export type StoreHealthCheck = components["schemas"]["StoreHealthCheckResponse"];
export type StoreHealthPanel = components["schemas"]["StoreHealthPanelResponse"];
export type StoreSettingsDiff = components["schemas"]["StoreSettingsDiffResponse"];
export type StoreSettingsDiffRow = components["schemas"]["StoreSettingsDiffRowResponse"];
export type StoreSettingsDiffOrigin = components["schemas"]["StoreSettingsDiffOriginEnum"];

/**
 * The four scorecard panels, in display order.
 *
 * Each is a separate endpoint on purpose — they differ in cost by orders of
 * magnitude, so one panel must never block or blank the others.
 */
export const STORE_HEALTH_PANELS = ["identity", "cameras", "planograms", "imports"] as const;

export type StoreHealthPanelName = (typeof STORE_HEALTH_PANELS)[number];

const PANEL_ENDPOINTS = {
  identity: "/v1/stores/{store_name}/health/identity",
  cameras: "/v1/stores/{store_name}/health/cameras",
  planograms: "/v1/stores/{store_name}/health/planograms",
  imports: "/v1/stores/{store_name}/health/imports",
} as const;

// Health is a point-in-time read of live pipelines. Cached briefly so switching
// tabs is instant, but short enough that a refetch after a fix shows the change.
const HEALTH_STALE_TIME = 60_000;

export function useStoreHealthPanelQuery(storeName: string, panel: StoreHealthPanelName) {
  return useQuery({
    queryKey: ["stores", "health", storeName, panel],
    staleTime: HEALTH_STALE_TIME,
    enabled: !!storeName,
    queryFn: async () => {
      const res = await api.GET(PANEL_ENDPOINTS[panel], {
        params: { path: { store_name: storeName } },
      });
      // An error status with an empty body leaves BOTH `error` and `data`
      // undefined, so checking `error` alone would resolve the query to
      // undefined and the card would then read `.checks` off it.
      if (res.error || !res.data) throw new Error(`Failed to load ${panel} health panel`);
      return res.data as StoreHealthPanel;
    },
  });
}

export function useStoreSettingsDiffQuery(storeName: string) {
  return useQuery({
    queryKey: ["stores", "health", storeName, "settings-diff"],
    staleTime: HEALTH_STALE_TIME,
    enabled: !!storeName,
    queryFn: async () => {
      const res = await api.GET("/v1/stores/{store_name}/health/settings-diff", {
        params: { path: { store_name: storeName } },
      });
      if (res.error || !res.data) throw new Error("Failed to load settings diff");
      return res.data as StoreSettingsDiff;
    },
  });
}
